import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import { NavRule } from './motion';
import { navLinks, companyInfo } from '../data/mock';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [ind, setInd] = useState({ x: 0, w: 0, on: false });
  const { pathname } = useLocation();
  const list = useRef(null);
  const links = useRef({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [open]);

  // sliding highlight: rests on the active page, follows the pointer
  const moveTo = (el) => el && setInd({ x: el.offsetLeft, w: el.offsetWidth, on: true });
  const rest = () => { const a = links.current[pathname]; a ? moveTo(a) : setInd((i) => ({ ...i, on: false })); };
  useLayoutEffect(() => { rest(); window.addEventListener('resize', rest); return () => window.removeEventListener('resize', rest); }, [pathname]); // eslint-disable-line

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 px-4 pt-3 sm:pt-4 pointer-events-none">
        <nav
          aria-label="Main"
          className={`pointer-events-auto mx-auto flex items-center justify-between transition-all duration-700 ${
            scrolled ? 'max-w-[62rem] h-16 rounded-full pl-6 pr-2 bg-[var(--glass)] backdrop-blur-xl border border-[var(--line-strong)] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)]' : 'max-w-[80rem] h-20 px-2 rounded-full border border-transparent'
          }`}
          style={{ transitionTimingFunction: 'var(--ease)' }}
        >
          <Link to="/" aria-label="enseQure home"><Logo className="h-9 w-auto" /></Link>

          <ul ref={list} onMouseLeave={rest} className="hidden md:flex relative items-center rounded-full border border-[var(--line)] bg-[var(--glass)] backdrop-blur-md p-1">
            <span aria-hidden className="absolute top-1 bottom-1 rounded-full bg-[var(--paper)] transition-all duration-500" style={{ left: ind.x, width: ind.w, opacity: ind.on ? 1 : 0, transitionTimingFunction: 'var(--ease)' }} />
            {navLinks.map((l, i) => {
              const active = pathname === l.href;
              const hovered = ind.on && links.current[l.href] && ind.x === links.current[l.href].offsetLeft;
              return (
                <li key={l.name} ref={(el) => (links.current[l.href] = el)} onMouseEnter={(e) => moveTo(e.currentTarget)}>
                  <Link to={l.href} aria-current={active ? 'page' : undefined} className={`relative z-10 flex items-baseline gap-1.5 px-5 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${hovered ? 'text-[var(--ink)]' : active ? 'text-[var(--paper)]' : 'text-[var(--muted)]'}`}>
                    <span className="font-mono-label !text-[0.6rem] opacity-60">0{i + 1}</span>{l.name}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/contact" className="btn btn-primary !py-2.5 !px-5 !hidden sm:!inline-flex">Talk to us</Link>
            <button className="md:hidden w-11 h-11 grid place-items-center rounded-full border border-[var(--line-strong)] bg-[var(--glass)]" onClick={() => setOpen(true)} aria-label="Open menu"><Menu className="w-5 h-5" /></button>
          </div>
        </nav>
        <NavRule />
      </header>

      {/* Full-screen mobile menu */}
      <div className={`md:hidden fixed inset-0 z-[70] bg-[var(--ink)] transition-all duration-500 ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`} aria-hidden={!open}>
        <div className="flex justify-between items-center px-6 h-20">
          <Logo className="h-9 w-auto" />
          <button onClick={() => setOpen(false)} aria-label="Close menu" className="w-11 h-11 grid place-items-center rounded-full border border-[var(--line-strong)]"><X className="w-5 h-5" /></button>
        </div>
        <ul className="px-6 pt-10">
          {navLinks.map((l, i) => (
            <li key={l.name} className="border-b border-[var(--line)]" style={{ transform: open ? 'none' : 'translateY(30px)', opacity: open ? 1 : 0, transition: `all 0.7s var(--ease) ${100 + i * 80}ms` }}>
              <Link to={l.href} className="flex items-baseline gap-4 py-5 font-display text-6xl"><span className="font-mono-label text-[var(--red)]">0{i + 1}</span>{l.name}</Link>
            </li>
          ))}
        </ul>
        <div className="absolute bottom-0 inset-x-0 p-6 space-y-4">
          <Link to="/contact" className="btn btn-primary w-full justify-center">Talk to us</Link>
          <p className="font-mono-label text-[var(--muted)] text-center">{companyInfo.email} · {companyInfo.phone}</p>
        </div>
      </div>
    </>
  );
};

export default Navbar;
